import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import PageIntro from "@/components/PageIntro";
import { Reveal } from "@/components/landing/Reveal";
import IMAGES from "@/data/treasuryImages.json";
import { ArtCredit } from "@/components/ArtCredit";
import { SAINT_QUOTATIONS } from "@/data/saintQuotations";

const SAINTS = [
  {
    id: "bernadette",
    name: "St. Bernadette Soubirous",
    life: "1844 – 1879",
    place: "Lourdes, France",
    icon: "https://commons.wikimedia.org/wiki/Special:FilePath/Bernadette%20Soubirous.jpg?width=600",
    role: "Visionary of the Immaculate Conception",
    lede: "A fourteen-year-old shepherd girl who could not yet read the catechism, and to whom the Mother of God confided her own most secret name.",
    text: "Bernadette was born into a poor miller’s family in Lourdes. Gathering firewood near Massabielle on 11 February 1858, she reported seeing a lady; eighteen encounters followed through 16 July. The sanctuary’s chronology records that on 25 March the lady identified herself as the Immaculate Conception. Bernadette later joined the Sisters of Charity at Nevers, where she lived a hidden life of prayer and service amid serious illness. She died in 1879, aged thirty-five. Her body is venerated in the chapel of Saint-Gildard.",
    witness: "bernadette",
    canonized: "Canonized by Pius XI, 8 December 1933 · feast 16 April",
  },
  {
    id: "juan-diego",
    name: "St. Juan Diego Cuauhtlatoatzin",
    life: "1474 – 1548",
    place: "Tepeyac, Mexico",
    icon: "https://commons.wikimedia.org/wiki/Special:FilePath/Juan-Diego.jpg?width=600",
    role: "First indigenous saint of the Americas",
    lede: "The Nahua widower to whom the Mother of God appeared as one of his own people — and left her portrait miraculously imprinted on his tilma.",
    text: "Juan Diego is remembered as the indigenous messenger in the Guadalupe narrative. The Nican Mopohua recounts his encounters with Mary at Tepeyac, her request for a church and the sign of flowers carried in his tilma before the bishop. The image is now venerated in the Basilica of Guadalupe. The account was first printed in 1649; its transmission and historical context are discussed in our Guadalupe entry. His canonization in 2002 honours his holiness without making every popular detail of the story an article of faith.",
    witness: "guadalupe",
    canonized: "Canonized by St. John Paul II, 31 July 2002 · feast 9 December",
  },
  {
    id: "fatima-children",
    name: "Sts. Francisco & Jacinta Marto",
    life: "1908–1919 · 1910–1920",
    place: "Fátima, Portugal",
    icon: "https://commons.wikimedia.org/wiki/Special:FilePath/Saints%20Francisco%20and%20Jacinta%20Marto%20double%20portrait.jpg?width=600",
    role: "Youngest non-martyr saints of the Church",
    lede: "A brother and sister of nine and seven, shepherds of Aljustrel, to whom the Lady of the Rosary confided the message of Fátima.",
    text: "With their cousin Lúcia, Francisco and Jacinta reported six Marian encounters between May and October 1917. Most took place at the Cova da Iria; the August encounter was at Valinhos. Their response centred on the Rosary, prayer for sinners and care for others. Both died young after illness during the influenza pandemic. John Paul II’s beatification homily presents Francisco’s desire to console Jesus and Jacinta’s generous concern for sinners. Their remains rest in the basilica at Fátima, alongside Lúcia’s.",
    witness: "francisco",
    canonized: "Canonized by Pope Francis at Fátima, 13 May 2017 · feast 20 February",
  },
  {
    id: "kolbe",
    name: "St. Maximilian Kolbe",
    life: "1894 – 1941",
    place: "Poland · Auschwitz",
    icon: "https://commons.wikimedia.org/wiki/Special:FilePath/Fr.Maximilian%20Kolbe%20in%201936.jpg?width=600",
    role: "Martyr of Auschwitz · Knight of the Immaculata",
    lede: "The Polish Conventual Franciscan who founded the Militia of the Immaculata and gave his life in the starvation bunker to save a fellow prisoner.",
    text: "A Conventual Franciscan with a deep Marian devotion, Kolbe founded the Militia Immaculatae in Rome in 1917, established Niepokalanów in Poland and undertook missionary work in Japan. At Auschwitz in 1941 he volunteered to take the place of a fellow prisoner condemned to starvation. He sustained the other prisoners through prayer and died on 14 August after a lethal injection. John Paul II’s canonization homily interprets this self-offering in the light of Christ’s love.",
    witness: "kolbe",
    canonized: "Canonized by St. John Paul II, 10 October 1982 · feast 14 August",
  },
  {
    id: "jp2",
    name: "St. John Paul II",
    life: "1920 – 2005",
    place: "Wadowice, Poland · Rome",
    icon: "https://commons.wikimedia.org/wiki/Special:FilePath/Pope%20John%20Paul%20II%20with%20Sr.%20Kate%20Kuenstler%20PHJC.jpg?width=600",
    role: "Pope · Totus Tuus",
    lede: "The great Marian pope — theologian of the body, evangelist of two continents, and the son who consecrated the whole world to the Immaculate Heart.",
    text: "Karol Wojtyła’s Marian spirituality was deeply shaped by St. Louis de Montfort. His motto Totus tuus expressed a belonging to Jesus through Mary, not a devotion detached from Christ. As pope he wrote Redemptoris Mater and Rosarium Virginis Mariae and proposed the Luminous Mysteries of the Rosary. His homily in Washington in 1979 presents Mary as a model of trust in God amid suffering and apparent contradiction. He died on 2 April 2005, the eve of Divine Mercy Sunday.",
    witness: "jp2",
    canonized: "Canonized by Pope Francis, 27 April 2014 · feast 22 October",
  },
  {
    id: "carlo-acutis",
    name: "St. Carlo Acutis",
    life: "1991 – 2006",
    place: "Milan · Assisi",
    icon: "https://commons.wikimedia.org/wiki/Special:FilePath/Covent%20Garden%2C%20Corpus%20Christi%20Catholic%20Church%2C%20Carlo%20Acutis%20memorial.jpg?width=600",
    role: "The 'cyber apostle' — patron of this treasury",
    lede: "A teenage Milanese boy who created an online exhibition of Eucharistic miracles, and who died of leukemia at fifteen offering his sufferings for the Pope and for the Church.",
    text: "Born and baptised in London, Carlo grew up in Milan. He enjoyed computers, video games, and ordinary friendships while making the Eucharist, daily prayer, and care for others central to his life. His online exhibition of Eucharistic miracles used technology to invite people to Christ; it is a direct inspiration for this treasury. He died of leukemia on 12 October 2006, aged fifteen. Pope Leo XIV canonized him with Pier Giorgio Frassati on 7 September 2025. The photograph here shows his memorial at Corpus Christi Church, Covent Garden, not a lifetime portrait.",
    witness: "carlo",
    canonized: "Canonized by Pope Leo XIV, 7 September 2025 · feast 12 October",
    source: "https://press.vatican.va/content/salastampa/en/bollettino/pubblico/2025/09/07/250907a.html",
  },
  {
    id: "montfort",
    name: "St. Louis-Marie Grignion de Montfort",
    life: "1673 – 1716",
    place: "Brittany & the Vendée, France",
    icon: "https://commons.wikimedia.org/wiki/Special:FilePath/Louis%20de%20Montfort.jpg?width=600",
    role: "Priest · missionary · apostle of the Rosary",
    lede: "The wandering French missionary who taught the Church to go 'to Jesus through Mary,' and whose little book on holy slavery to Our Lady formed saints and popes.",
    text: "Born at Montfort-sur-Meu in Brittany, Louis-Marie gave away his possessions to the poor and was ordained in 1700. Barred from many pulpits for his fiery zeal, he preached parish missions across western France, rebuilding faith through the Rosary and the renewal of baptismal promises made as a total consecration to Jesus through Mary. He founded the Company of Mary and the Daughters of Wisdom, and died worn out at 43 while preaching at Saint-Laurent-sur-Sèvre. His masterwork, hidden in a chest, was found only in 1842 — and shaped the Marian devotion of St. John Paul II, whose motto Totus tuus is drawn straight from its pages.",
    witness: "montfort",
    books: [
      { title: "True Devotion to Mary", note: "His treatise on total consecration — \u2018to Jesus through Mary.\u2019" },
      { title: "The Secret of the Rosary", note: "A short, practical guide to praying the Rosary well." },
    ],
    canonized: "Canonized by Pius XII, 20 July 1947 · feast 28 April",
  },
];

export default function SaintsPage() {
  return (
    <main data-testid="saints-page">
      <Seo
        title="Saints & Witnesses — lives shaped by devotion to Mary · Theotokos · Mater Dei"
        siteName="Theotokos · Mater Dei"
        description="Eight saints shaped by love of the Mother of God: Bernadette, Juan Diego, Francisco and Jacinta Marto, Maximilian Kolbe, John Paul II, Carlo Acutis, and St. Louis de Montfort, apostle of the Rosary."
      />
      <PageIntro
        crumbs={[{ label: "Home", to: "/" }, { label: "Discover", to: "/apologetics" }, { label: "Saints & Witnesses" }]}
        eyebrow="Discover · Saints & witnesses"
        lines={["Lives shaped", "by the Mother of God"]}
        aside={
          <div className="rounded-2xl border border-marian/15 bg-white/70 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">A cloud of witnesses</p>
            <p data-testid="saints-count" className="mt-2 font-serif text-lg leading-snug text-midnight">Eight saints — visionaries, martyrs, a pope, a missionary of the Rosary, and a teenager with a laptop.</p>
          </div>
        }
      >
        Every Marian devotion has a face. These eight saints are remembered here because the Mother of God
        shaped their whole existence and because, together, they tell you what the treasury of Mary
        looks like when it is lived.
      </PageIntro>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:px-16">
        <ol className="space-y-16" data-testid="saints-list">
          {SAINTS.map((s, i) => {
            const flip = i % 2 === 1;
            const witness = SAINT_QUOTATIONS[s.witness];
            const WitnessBody = witness.kind === "paraphrase" ? "div" : "blockquote";
            return (
              <li key={s.id} id={s.id} className="scroll-mt-28" data-testid={`saint-${s.id}`}>
                <Reveal>
                  <article className={`grid gap-8 lg:grid-cols-[0.45fr_0.55fr] lg:items-center ${flip ? "lg:[&>div:first-child]:order-2" : ""}`}>
                    <div className="overflow-hidden rounded-3xl border border-marian/15 bg-white shadow-[0_28px_60px_-40px_rgba(16,42,67,0.5)]">
                      <div className="aspect-[4/5] overflow-hidden bg-paleblue/40">
                        <img
                          src={IMAGES[s.id]?.src || s.icon}
                          alt={s.id === "carlo-acutis" ? "Memorial of St. Carlo Acutis at Corpus Christi Church, Covent Garden" : `Portrait of ${s.name}`}
                          data-testid={`saint-image-${s.id}`}
                          loading="lazy"
                          className="h-full w-full object-contain"
                        />
                      </div>
                      <div className="border-t border-marian/10 px-5 py-4">
                        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">{s.life}</p>
                        <p className="mt-1 text-xs text-slateblue">{s.place}</p>
                        <div className="mt-3"><ArtCredit imageId={s.id} prefix={`saint-${s.id}`} /></div>
                      </div>
                    </div>

                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">{s.role}</p>
                      <h2 className="mt-2 font-serif text-3xl font-semibold tracking-tight text-midnight sm:text-4xl">{s.name}</h2>
                      <p className="mt-4 max-w-xl font-serif text-lg italic leading-relaxed text-midnight/85">{s.lede}</p>
                      <p className="mt-5 max-w-2xl text-base leading-relaxed text-charcoal/90 sm:text-lg">{s.text}</p>
                      <WitnessBody data-testid={`saint-quote-${s.id}`} className="mt-6 border-l-2 border-gold pl-4 font-serif text-lg italic text-midnight">
                        {witness.kind === "paraphrase" ? witness.text : `“${witness.text}”`}
                        <p data-testid={`saint-quote-author-${s.id}`} className="mt-2 font-sans text-xs not-italic text-slateblue">{witness.author}</p>
                        <a href={witness.url} target="_blank" rel="noreferrer" data-testid={`saint-quote-source-${s.id}`} className="mt-2 block font-sans text-xs not-italic leading-relaxed text-marian underline underline-offset-4 transition-colors hover:text-midnight">{witness.source}</a>
                      </WitnessBody>
                      {s.books && (
                        <div className="mt-6" data-testid={`saint-books-${s.id}`}>
                          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Two books to read</p>
                          <ul className="mt-3 space-y-2">
                            {s.books.map((b) => (
                              <li key={b.title} className="text-sm text-charcoal/90">
                                <span className="font-serif text-base font-semibold italic text-midnight">{b.title}</span>
                                <span className="text-slateblue"> — {b.note}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <p data-testid={`saint-canonization-${s.id}`} className="mt-4 font-mono text-[10px] uppercase tracking-[0.24em] text-slateblue">{s.canonized}</p>
                      {s.source && <a href={s.source} target="_blank" rel="noopener noreferrer" data-testid={`saint-source-${s.id}`} className="mt-3 inline-flex text-xs text-marian underline underline-offset-4 transition-colors hover:text-gold">Holy See · Canonization homily</a>}
                    </div>
                  </article>
                </Reveal>
              </li>
            );
          })}
        </ol>

        <Reveal>
          <div className="mt-20 rounded-3xl border border-marian/15 bg-paleblue/40 p-6 sm:p-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Also read</p>
            <p className="mt-2 font-serif text-2xl leading-snug text-midnight sm:text-3xl">
              Every apparition entry names the visionary and links to her or his life —{" "}
              <Link to="/apparitions" data-testid="saints-to-apparitions" className="text-marian underline decoration-gold decoration-2 underline-offset-4 hover:text-midnight">
                explore the apparitions and devotions
              </Link>
              .
            </p>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
